import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS, STORE_PHONE } from "@/constants/store";
import { Handshake, Bell, BadgePercent, FileCheck } from "lucide-react";

const PERKS = [
  { icon: Handshake, t: "Free to join", d: "No membership fee, no minimum purchase." },
  { icon: Bell, t: "First look at every load", d: "New-load text alerts before anything hits the sales floor — pallets and bulk lots included." },
  { icon: BadgePercent, t: "Volume pricing", d: "Better pricing on multi-unit purchases." },
  { icon: FileCheck, t: "Tax-free resale purchases", d: "No Michigan sales tax on qualifying purchases once your Form 3372 is on file — BEFORE you buy, not after." },
];

export default function ResellersPage() {
  usePageMeta("Reseller Program", "Join the IPVALUE MARKETPLACE Reseller Program — first look at loads, volume pricing, tax-free resale purchases.");
  const [form, setForm] = useState({ name: "", business_name: "", phone: "", email: "", tax_license: "", buys: "", text_alerts: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business_name || !form.phone) {
      toast.error("Name, business name and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/resellers", { ...form, email: form.email || null });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — call us instead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="resellers-page">
      <section className="bg-[#0A1C40] border-b-4 border-[#FFC220]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-14">
          <div className="ipv-eyebrow text-[#FFC220]">Wholesale · Resellers</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white mt-3">
            Buy it first. Sell it on.
          </h1>
          <p className="text-white/70 mt-4 max-w-xl">
            Our Reseller Program gives flippers, store owners, and exporters first access to every new load —
            free to join, same-day setup.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-8 py-12 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="grid sm:grid-cols-2 gap-3">
            {PERKS.map((p) => (
              <div key={p.t} className="border-2 border-gray-200 p-5">
                <p.icon className="w-6 h-6 text-[#C9971E]" />
                <div className="font-display font-bold mt-3">{p.t}</div>
                <div className="text-sm text-gray-600 mt-1">{p.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-gray-50 border border-gray-200 p-5 text-sm leading-relaxed" data-testid="reseller-steps">
            <div className="font-display font-black text-[#0A1C40]">Join in 3 steps — same day:</div>
            <ol className="mt-2 space-y-1 list-decimal list-inside text-gray-700">
              <li>Bring your Michigan Sales Tax License number</li>
              <li>Complete Michigan Form 3372 (blanks at the counter)</li>
              <li>Give us your name, business name, phone, and what you buy</li>
            </ol>
            <div className="mt-2 text-xs text-gray-500">
              Form 3372 must be on file BEFORE a tax-free purchase — visit us at {STORE_ADDRESS.short} or call {STORE_PHONE}.
            </div>
          </div>
        </div>

        <div className="border-2 border-[#0A1C40] p-6 h-fit" data-testid="reseller-form">
          {done ? (
            <div className="text-center py-10" data-testid="reseller-success">
              <div className="font-display font-black text-2xl text-[#0A1C40]">You're on the list! 🎉</div>
              <p className="text-sm text-gray-600 mt-3">
                We'll text you before new loads hit the floor. Bring your Michigan Sales Tax License and finish
                Form 3372 at the counter to unlock tax-free resale purchases.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="ipv-eyebrow">Sign up online</div>
              <input className="input-base" placeholder="Your name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reseller-name" />
              <input className="input-base" placeholder="Business name *" value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="reseller-business" />
              <input className="input-base" placeholder="Phone *" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reseller-phone" />
              <input className="input-base" placeholder="Email (optional)" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reseller-email" />
              <input className="input-base" placeholder="MI Sales Tax License # (or bring it in)" value={form.tax_license}
                onChange={(e) => setForm({ ...form, tax_license: e.target.value })} data-testid="reseller-license" />
              <textarea className="input-base" rows={2} placeholder="What do you buy? (furniture, pallets, electronics…)" value={form.buys}
                onChange={(e) => setForm({ ...form, buys: e.target.value })} data-testid="reseller-buys" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.text_alerts}
                  onChange={(e) => setForm({ ...form, text_alerts: e.target.checked })} data-testid="reseller-alerts" />
                Text me new-load alerts before they hit the sales floor
              </label>
              <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="reseller-submit">
                {busy ? "Sending…" : "Join the Reseller Program"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
